import type { KanbanBoard } from '@pm/shared';
import { normalizeKanbanState } from '../../../../api/hooks/useKanban';

/** Schlüssel des Sammelsegments für Karten ohne (bekanntes) Arbeitspaket. */
export const NO_AP_KEY = '__ohne_ap__';
export const NO_AP_LABEL = 'Ohne Arbeitspaket';

/** Minimaler AP-Eintrag (Herkunft: useArbeitspakete). */
export interface ApLite { id: string; nr: string; titel: string | null; }

export interface BarSegment {
  apKey: string;
  count: number;
  color: string;
  /** Untere/obere Kante des Segments im Stapel (in Karten). */
  bottom: number;
  top: number;
}

export interface ClosedBoardBar {
  key: string;
  label: string;
  /** ISO-Zeitstempel (Näherung, siehe closingDate). */
  closedAt: string;
  total: number;
  segments: BarSegment[];
}

export interface LegendEntry { apKey: string; label: string; color: string; }

export interface Ribbon {
  apKey: string;
  color: string;
  fromIndex: number;
  toIndex: number;
  from: { bottom: number; top: number };
  to: { bottom: number; top: number };
}

const PALETTE = [
  '#1D8296', '#E08A1E', '#7B5EA7', '#4C9A2A', '#C8453B',
  '#2F6FB3', '#B5A100', '#D1649B', '#5F7D8C', '#8C5A3C',
];
const NO_AP_COLOR = '#9E9E9E';

/**
 * Schließdatum eines eingefrorenen Boards. Das Backend speichert keinen eigenen
 * Zeitstempel fürs Schließen. Beim Einfrieren („Neues Board") entsteht jedoch im selben
 * Zug das Nachfolger-Board desselben Arbeitspakets — dessen created_at ist die beste
 * Näherung. Fehlt der Nachfolger, wird updated_at des Boards verwendet.
 */
function closingDate(board: KanbanBoard, all: ReadonlyArray<KanbanBoard>): string {
  const successor = all
    .filter((b) => b.ap_id === board.ap_id && b.seq > board.seq)
    .sort((a, b) => a.seq - b.seq)[0];
  return successor?.created_at ?? board.updated_at;
}

/**
 * Verdichtet die Boards zu Balken: ein Balken je Label geschlossener (eingefrorener) Boards,
 * Höhe = bei Schließung erledigte Karten (sichtbar, nicht abgebrochen), gestapelt nach
 * Arbeitspaket. Boards mehrerer Arbeitspakete mit gleichem Label (z. B. „KW 10") bilden
 * gemeinsam einen Balken.
 */
export function aggregateClosedBoards(
  boards: ReadonlyArray<KanbanBoard>,
  aps: ReadonlyArray<ApLite>,
): { bars: ClosedBoardBar[]; legend: LegendEntry[] } {
  const sortedAps = [...aps].sort((a, b) => a.nr.localeCompare(b.nr, undefined, { numeric: true, sensitivity: 'base' }));
  const apIndex = new Map(sortedAps.map((a, i) => [a.id, i]));
  const keyOf = (apId: string | null | undefined) => (apId && apIndex.has(apId) ? apId : NO_AP_KEY);
  const colorOf = (key: string) => (key === NO_AP_KEY ? NO_AP_COLOR : PALETTE[apIndex.get(key)! % PALETTE.length]!);
  const orderOf = (key: string) => (key === NO_AP_KEY ? Number.MAX_SAFE_INTEGER : apIndex.get(key)!);

  const groups = new Map<string, { label: string; closedAt: string; counts: Map<string, number> }>();
  for (const board of boards) {
    if (!board.frozen) continue;
    const label = board.label.trim();
    const closedAt = closingDate(board, boards);
    const g = groups.get(label) ?? { label, closedAt, counts: new Map<string, number>() };
    if (closedAt > g.closedAt) g.closedAt = closedAt;
    for (const c of board.cards) {
      if (c.hidden || c.state === 'CANCELED') continue;
      if (normalizeKanbanState(c.state) !== 'COMPLETED') continue;
      const key = keyOf(c.ap_id);
      g.counts.set(key, (g.counts.get(key) ?? 0) + 1);
    }
    groups.set(label, g);
  }

  const used = new Set<string>();
  const bars: ClosedBoardBar[] = [...groups.values()]
    .sort((a, b) => a.closedAt.localeCompare(b.closedAt))
    .map((g) => {
      let offset = 0;
      const segments: BarSegment[] = [...g.counts.entries()]
        .sort(([a], [b]) => orderOf(a) - orderOf(b))
        .map(([apKey, count]) => {
          used.add(apKey);
          const seg = { apKey, count, color: colorOf(apKey), bottom: offset, top: offset + count };
          offset += count;
          return seg;
        });
      return { key: g.label, label: g.label, closedAt: g.closedAt, total: offset, segments };
    });

  const apById = new Map(sortedAps.map((a) => [a.id, a]));
  const legend: LegendEntry[] = [...used]
    .sort((a, b) => orderOf(a) - orderOf(b))
    .map((apKey) => {
      const ap = apById.get(apKey);
      const label = !ap ? NO_AP_LABEL : `AP ${ap.nr}${ap.titel ? ` · ${ap.titel}` : ''}`;
      return { apKey, label, color: colorOf(apKey) };
    });

  return { bars, legend };
}

/** Schrägen zwischen benachbarten Balken: gleiches Arbeitspaket-Segment, Kante zu Kante. */
export function buildRibbons(bars: ReadonlyArray<ClosedBoardBar>): Ribbon[] {
  const ribbons: Ribbon[] = [];
  for (let i = 0; i + 1 < bars.length; i++) {
    for (const seg of bars[i]!.segments) {
      const next = bars[i + 1]!.segments.find((s) => s.apKey === seg.apKey);
      if (!next) continue;
      ribbons.push({
        apKey: seg.apKey, color: seg.color, fromIndex: i, toIndex: i + 1,
        from: { bottom: seg.bottom, top: seg.top },
        to: { bottom: next.bottom, top: next.top },
      });
    }
  }
  return ribbons;
}
